import React from 'react';
import {
    Box, Dialog, DialogActions, DialogContent, DialogTitle,
    IconButton, TextField, Typography, useTheme
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import {observer} from 'mobx-react';
import FilledButton from "../../../components/buttons/FilledButton";
import ReadOnlyField from "../../../components/fields/ReadOnlyField";
import TwoDecimalsMoneyRenderer from "../../../utils/TwoDecimalsMoneyRenderer";
import CustomerContextPanel from "./CustomerContextPanel";

function PaymentReminderModal({feature}) {
    const theme = useTheme();

    return (
        <Dialog open={feature.isOpen} onClose={() => feature.close()} maxWidth="md" fullWidth>
            <DialogTitle sx={{display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}>
                Recordatorio de pago
                <IconButton onClick={() => feature.close()}>
                    <CloseIcon/>
                </IconButton>
            </DialogTitle>
            <DialogContent>
                {feature.errorMessage &&
                    <Typography variant="body2" sx={{color: theme.palette.error.main, mb: 2}}>
                        {feature.errorMessage}
                    </Typography>}

                {feature.hasPreview && <PaymentReminderDraft feature={feature} theme={theme}/>}
            </DialogContent>
            <DialogActions>
                <FilledButton onClick={() => feature.close()}>
                    Cancelar
                </FilledButton>
                {feature.hasPreview &&
                    <FilledButton onClick={() => feature.send()} disabled={!feature.canSend}>
                        Enviar
                    </FilledButton>}
            </DialogActions>
        </Dialog>
    );
}

const PaymentReminderDraft = observer(({feature, theme}) => {
    return (
        <Box sx={{display: 'flex', gap: 3, alignItems: 'flex-start'}}>
            <Box sx={{flex: 2}}>
                <ReadOnlyField label="Destinatarios" value={feature.receivers.join(', ')}/>
                <ReadOnlyField label="CCO" value={feature.blindCopyReceivers.join(', ')}/>
                <ReadOnlyField label="Asunto" value={feature.subject}/>

                <TextField
                    label="Mensaje"
                    fullWidth
                    multiline
                    minRows={3}
                    value={feature.message}
                    onChange={(event) => feature.setMessage(event.target.value)}
                    sx={{mt: 2, mb: 2}}
                />

                <OverdueInvoicesDetail feature={feature} theme={theme}/>
            </Box>
            <Box sx={{flex: 1}}>
                <CustomerContextPanel paymentTrackRecord={feature.paymentTrackRecord}/>
            </Box>
        </Box>
    );
});

function reminderHistoryDescription(reminderHistory) {
    if (!reminderHistory.hasReminders) {
        return 'Sin recordatorios previos';
    }
    if (reminderHistory.count === 1) {
        return `Enviado el ${reminderHistory.firstReminderSentAt}`;
    }
    return `Primer envío ${reminderHistory.firstReminderSentAt} · Último ${reminderHistory.lastReminderSentAt} · ${reminderHistory.count} envíos`;
}

function OverdueInvoicesDetail({feature, theme}) {
    if (feature.overdueInvoices.length === 0) {
        return null;
    }
    return (
        <Box sx={{border: `1px solid ${theme.palette.divider}`, borderRadius: '8px', p: 2}}>
            <Typography variant="subtitle2" sx={{mb: 1}}>Facturas vencidas</Typography>
            {feature.overdueInvoices.map(overdueInvoice => (
                <Box key={overdueInvoice.number} sx={{mb: 1}}>
                    <Typography variant="body2">
                        {overdueInvoice.number} — vence {overdueInvoice.expirationDate} —
                        {' '}{overdueInvoice.daysOverdue} días de atraso
                    </Typography>
                    <Typography variant="body2">
                        Total {TwoDecimalsMoneyRenderer(overdueInvoice.total.amount)} —
                        {' '}saldo pendiente {TwoDecimalsMoneyRenderer(overdueInvoice.pendingBalance.amount)}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                        {reminderHistoryDescription(overdueInvoice.reminderHistory)}
                    </Typography>
                </Box>
            ))}
            {feature.totalOverdue &&
                <Typography variant="subtitle2" sx={{mt: 1}}>
                    Total vencido: {TwoDecimalsMoneyRenderer(feature.totalOverdue.amount)}
                </Typography>}
        </Box>
    );
}

export default observer(PaymentReminderModal);
